import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import {
  Award,
  BookOpen,
  Briefcase,
  Building,
  Calendar,
  FileText,
  GraduationCap,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { fileToBase64 } from "../../../api/authService";
import professionalProfileApi from "../../../api/professionalProfile";
import Swal from "sweetalert2";

import * as Sentry from "@sentry/react";
import AlertDialogSlide from "../../../components/common/AlertDialog";
import CustomLoader from "../../../components/common/CustomLoader";
import OptionDisplay from "../../../components/common/OptionDisplay";
import CertificatePreview from "../../../components/professional/CertificatePreview";
import EmptyStateCard from "../../../components/professional/EmptyStateCard";
import Modal from "../../../components/professional/Modal";
import {
  licenseRatingOptions,
  regulatoryBodyOptions,
} from "../../../utils/professionalDropdownOptions";
import ExperienceForm from "./ExperienceForm";
import ClampedText from "../../../components/common/ClampedText";

const ExperienceTab = ({
  experiences,
  onModification,
  onDocumentUpload,
  setNotification,
  dropDownOptions,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [errors, setErrors] = useState({});
  const [openAlertDialog, setAlertOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasModified, setHasModified] = useState(false);

  const handleOpen = (experience = null) => {
    setSelectedExperience(experience);
    setOpen(true);
    setErrors({});
    setHasModified(false);
  };

  const handleClose = () => {
    setSelectedExperience(null);
    setOpen(false);
    setErrors({});
    if (hasModified) {
      setHasModified(false);
      if (onModification) onModification();
      if (onDocumentUpload) onDocumentUpload();
    }
  };

  const handleSave = async (tabPayload, shouldClose = true) => {
    console.log(tabPayload);

    try {
      // tabPayload is one of:
      //   { experience: [...] }  |  { licenses: [...] }  |  { medicals: [...] }

      let res;
      let results = [];
      let newLicenseFiles = [];

      if (tabPayload.experience) {

        results = await Promise.all(
          tabPayload.experience.map((item) => {
            const { id, ...rest } = item;
            if (id) {
              return professionalProfileApi.updateSingleResource(id, 'experience', rest);
            }
            return professionalProfileApi.createExperience(rest);
          })
        );
        res = results[results.length - 1];
      } else if (tabPayload.licenses) {
        results = await Promise.all(
          tabPayload.licenses.map(async (item) => {
            const { id, ...rest } = item;

            if (rest.license_upload instanceof File) {
              try {
                await professionalProfileApi.createDocuments({
                  category: "aviation_licenses",
                  files: [rest.license_upload],
                });
              } catch (docErr) {
                console.error("Error syncing license document:", docErr);
              }
            } else if (!rest.license_upload) {
              rest.license_upload = null;
            }

            if (id) {
              return professionalProfileApi.updateSingleResource(id, 'licenses', rest);
            }
            return professionalProfileApi.createLicense(rest);
          })
        );
        res = results[results.length - 1];
      } else if (tabPayload.medicals) {
        results = await Promise.all(
          tabPayload.medicals.map((item) => {
            const { id, ...rest } = item;

            if (rest.medical_upload instanceof File) {
              try {
                professionalProfileApi.createDocuments({
                  category: "medical_certificate",
                  files: [rest.medical_upload],
                });
              } catch (docErr) {
                console.error("Error syncing medical document:", docErr);
              }
            } else if (!rest.medical_upload) {
              rest.medical_upload = null;
            }

            if (id) {
              // medicals are stored in licenses table in backend
              return professionalProfileApi.updateSingleResource(id, 'licenses', rest);
            }
            return professionalProfileApi.createMedical(rest);
          })
        );
        res = results[results.length - 1];
      }

      if (res && (res.status === 200 || res.status === 201)) {
        setHasModified(true);
        const tabLabel = tabPayload.experience
          ? "Experience"
          : tabPayload.licenses
            ? "License"
            : "Medical";

        if (shouldClose) {
          await Swal.fire({
            icon: 'success',
            title: 'Success',
            text: `${tabLabel} saved successfully!`,
            confirmButtonColor: '#1976d2',
            confirmButtonText: 'OK',
          });
          if (onModification) onModification();
          if (onDocumentUpload) onDocumentUpload();
          handleClose();
        } else {
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: `${tabLabel} saved successfully!`,
            timer: 1500,
            timerProgressBar: true,
            confirmButtonColor: '#1976d2',
            confirmButtonText: 'OK',
          });
        }
        return results ? results.map((r) => r?.data || r) : true;
      }
      return false;
    } catch (error) {
      Sentry.captureException(error, "Error saving experiences");
      console.error(error);

      if (error?.response?.data) {
        const errorData = error.response.data;
        // Check for specific wrappers (old format) or direct errors (new format)
        let errorObj = errorData.experiences?.[0] || errorData.licenses?.[0] || errorData.medicals?.[0] || errorData;

        // Exclude standard DRF or custom string messages that aren't field dicts
        if (typeof errorObj === 'object' && !Array.isArray(errorObj) && Object.keys(errorObj).length > 0) {
          const errorMsg = Object.entries(errorObj)
            .map(([key, val]) => `${key.replace(/_/g, ' ')}: ${Array.isArray(val) ? val.join(', ') : val}`)
            .join('\n');

          Swal.fire({ icon: 'error', title: 'Validation Error', text: errorMsg });
          setErrors(errorObj);
          return;
        }
      }

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while saving',
        confirmButtonColor: '#1976d2'
      });
    }
  };

  const handleDelete = async (experience) => {
    setLoading(true);
    try {
      if (experience.isStandaloneLicense) {
        await professionalProfileApi.deleteTabEntry("licenses", experience.license_id);
      } else {
        await professionalProfileApi.deleteTabEntry("experience", experience.id);
      }
      setNotification("Entry deleted", "success");
      onModification();
    } catch (error) {
      Sentry.captureException(error, "Experience Delete failed.");
      if (error?.response?.data?.error)
        return setNotification(error.response.data.error, "error");
      setNotification("Delete failed", "error");
      // console.error("delete error",error)
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <CustomLoader />;

  const licenseFileUrl =
    import.meta.env.VITE_BACKEND_DOMAIN || "http://localhost:8000";

  const dialogTitle = selectedExperience ? "Edit Experience" : "Add Experience";

  return (
    <div>
      {/* Header with Add Button */}
      {experiences?.length === 0 ? (
        <>
          {/* <TransitionAlert message="Add your educational background and aviation certifications. This information helps employers assess your qualifications." /> */}
          <EmptyStateCard
            icon={<Briefcase size={48} color="#1976d2" />}
            title="No experience added yet"
            description="Add your experience to highlight 
        your professional credentials. This helps employers verify your 
        qualifications and compliance with regulatory requirements."
            buttonText="Add Experience"
            handleOpen={handleOpen}
          />
        </>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
              mb: 3,
            }}
          >
            <Typography variant="h6">Experiences</Typography>
            <Button
              startIcon={<Plus size={18} />}
              variant="contained"
              color="primary"
              onClick={() => handleOpen()}
            >
              Add Experience
            </Button>
          </Box>

          {/* Qualification List */}
          <Grid container spacing={3}>
            {experiences?.map((experience) => {
              const uniqueId = experience.isStandaloneLicense ? `lic-${experience.license_id}` : `exp-${experience.id}`;
              const isMedicalOnly = experience.isStandaloneLicense && !experience.license_type && experience.medical_class;
              const cardLabel = experience.isStandaloneLicense ? (isMedicalOnly ? "Medical" : "License") : "Experience";
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={uniqueId}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                      transition: "0.2s",
                      "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.15)" },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <CardContent>
                      {/* License Type */}
                      {/* Card Label */}
                      <Typography variant="overline" color="primary" sx={{ fontWeight: 600, display: "block", mb: 1 }}>
                        {cardLabel}
                      </Typography>

                      {/* Pilot Type (Only for Experience) */}
                      {!experience.isStandaloneLicense && (
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                          <ClampedText>{experience?.pilot_type || "Pilot Type"}</ClampedText>
                        </Box>
                      )}

                      {!experience.isStandaloneLicense && (
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                          <ClampedText>{experience?.years_of_experience ? `${experience.years_of_experience} Years of experience` : "Years of experience"}</ClampedText>
                        </Box>
                      )}

                      {/* Number */}
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 1,
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "400px",
                          }}
                        >
                          Number: {isMedicalOnly
                            ? (experience?.medical_certificate_number || "Not Specified")
                            : (experience?.license_number || "Not Specified")}
                        </Typography>
                      </Box>

                      {/* Status */}
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 1,
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "400px",
                          }}
                        >
                          Status: {isMedicalOnly
                            ? (experience?.medical_status || "Not Specified")
                            : (experience?.license_status_display || experience.license_status || "Not Specified")}
                        </Typography>
                      </Box>

                      {/* Medical Section Summary */}
                      {(experience.medical_certificate_number || experience.medical_class) && !isMedicalOnly && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: "1px dashed #eee" }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: "primary.main", display: "block", mb: 0.5 }}>
                            Medical Certificate
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Num: {experience.medical_certificate_number || "N/A"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="caption" color="text.secondary">
                              Status: {experience.medical_status || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {/* Document Upload */}
                      {experience.license_upload && !isMedicalOnly && (
                        <CertificatePreview
                          certificateUrl={licenseFileUrl}
                          filePath={experience.license_upload}
                        />
                      )}
                      {experience.medical_upload && isMedicalOnly && (
                        <CertificatePreview
                          certificateUrl={licenseFileUrl}
                          filePath={experience.medical_upload}
                        />
                      )}
                    </CardContent>

                    {/* Actions */}
                    <CardActions sx={{ justifyContent: "flex-end" }}>
                      <Button
                        color="primary"
                        size="small"
                        onClick={() => handleOpen(experience)}
                      >
                        Edit
                      </Button>
                      <AlertDialogSlide
                        description="Are you sure you want to delete this entry?"
                        title="Confirm deletion"
                        onConfirm={() => handleDelete(experience)}
                        open={openAlertDialog}
                        setOpen={setAlertOpenDialog}
                        confirmTitle="Confirm Delete"
                      >
                        <Button
                          color="error"
                          size="small"
                          onClick={() => setAlertOpenDialog(true)}
                        >
                          Delete
                        </Button>
                      </AlertDialogSlide>
                    </CardActions>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </>
      )}
      {/* Add/Edit Dialog */}
      <Modal handleClose={handleClose} open={open} title={dialogTitle}>
        <ExperienceForm
          experience={selectedExperience}
          onSubmit={handleSave}
          onCancel={handleClose}
          certificateUrl={licenseFileUrl}
          errors={errors}
          setErrors={setErrors}
          dropDownOptions={dropDownOptions}
        />
      </Modal>
    </div>
  );
};

export default ExperienceTab;
